<script>
/* eslint-disable prefer-destructuring */
import Vue from "vue";
import { mapActions, mapGetters, mapState } from "vuex";
import { throttle as _throttle } from "lodash";
import ApiHelper from "JS/helpers/civicactioncenter/apiHelper";
import FormHelper from "JS/helpers/FormHelper";
import AxiosHelper from "JS/helpers/axios";
import RedirectHelper from "JS/helpers/redirectHelper";
import EventsMixin from "JS/mixins/campaign/civicactioncenter/events";
import TrackingMixin from "JS/mixins/campaign/tracking";
import FormValidationMixin from "JS/mixins/campaign/formValidation";
import VeeValidate from "vee-validate";

import CacHeader from "CivicActionCenterComponents/layout/CACHeader.vue";
import CacBreadcrumbs from "CivicActionCenterComponents/layout/CACBreadcrumbs.vue";
import CacFooter from "CivicActionCenterComponents/layout/CACFooter.vue";
import { i18n } from "JS/i18n";
import BREAKPOINTS from "Constants/campaign/civicactioncenter/breakpoints";
import STATE from "Constants/campaign/civicactioncenter/state";
import TRACKING from "Constants/campaign/civicactioncenter/tracking";

Vue.use(VeeValidate, { events: "blur", inject: false });

const eventsBus = new Vue({});

const formValueContext = `${STATE.SCOPE_FORMS}/${STATE.FORM_VALUES}`;
const optionalFieldsContext = `${STATE.SCOPE_FORMS}/${STATE.OPTIONAL_FIELDS}`;
const selectedLanguageContext = `${STATE.SCOPE_CAC}/${STATE.SELECTED_LANGUAGE}`;
const isMobileContext = `${STATE.SCOPE_CAC}/${STATE.IS_MOBILE}`;
const broadcastDimensionsContext = `${STATE.SCOPE_CAMPAIGN}/${
  STATE.BROADCAST_DIMENSIONS
}`;
const advocateCookieInfoContext = `${STATE.SCOPE_CAC}/${
  STATE.ADVOCATE_COOKIE_INFO
}`;
const advocateRecordContext = `${STATE.SCOPE_FORMS}/${STATE.ADVOCATE_RECORD}`;
const redirectHelper = new RedirectHelper();

export default {
  name: "CivicActionCenter",
  components: {
    CacHeader,
    CacBreadcrumbs,
    CacFooter
  },
  mixins: [EventsMixin, TrackingMixin, FormValidationMixin],
  props: {
    advocateInfo: {
      type: Object,
      required: true
    },
    customerSettings: {
      type: Object,
      required: true
    },
    settings: {
      type: Object,
      required: true
    },
    appSettings: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      trackingPixelImages: []
    };
  },
  i18n,
  computed: {
    ...mapState({
      isMobile: isMobileContext
    }),
    ...mapGetters({
      selectedLanguage: selectedLanguageContext,
      advocateRecord: advocateRecordContext
    })
  },
  watch: {
    $route: "broadcastDimensions"
  },
  async created() {
    this.template = Vue.compile(
      window.P2A.campaignSettings.templates[this.$options.name]
    ).render;

    let selectedLocalLanguage;
    try {
      selectedLocalLanguage =
        window.localStorage.getItem("selectedLanguage") ||
        this.appSettings.language.toLowerCase();
    } catch (e) {
      // If accessing localStorage fails, private browsing is enabled
      selectedLocalLanguage = this.appSettings.language.toLowerCase();
    }
    this.setSelectedLanguage(selectedLocalLanguage);
    this.setInitialOptInStates();

    this.handleResize = _throttle(this.handleResize.bind(this), 2000);
    window.addEventListener("resize", this.handleResize, { passive: true });
    this.handleResize();
    this.setAdvocateCookieInfo(this.advocateInfo);
    this.setAdvocateRecord(this.advocateInfo);
    this.saveOptionalFields();
    this.initializeTracking();
    this.setHoneypotPageLoadTime(this.appSettings.loadStart);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize, { passive: true });
  },
  provide() {
    return {
      apiHelper: new ApiHelper(
        this.appSettings.shortUrl,
        this.customerSettings.id,
        this.$store
      ),
      axiosHelper: new AxiosHelper(this.unknownErrorMessage),
      formHelper: new FormHelper(this.$store, this.settings.formFields),
      redirectHelper,
      eventsBus
    };
  },
  methods: {
    ...mapActions({
      broadcastDimensions: broadcastDimensionsContext,
      setFormValue: formValueContext,
      setOptionalFields: optionalFieldsContext,
      setSelectedLanguage: selectedLanguageContext,
      setIsMobile: isMobileContext,
      setAdvocateCookieInfo: advocateCookieInfoContext,
      setAdvocateRecord: advocateRecordContext,
      setHoneypotPageLoadTime: "forms/honeypotPageLoadTime"
    }),
    setInitialOptInStates() {
      const subscribeEmail = this.customerSettings.subscriptionDefaultEmail;
      const subscribeSms = this.customerSettings.subscriptionDefaultSms;
      this.setFormValue({ fieldName: "subscribeEmail", value: subscribeEmail });
      this.setFormValue({ fieldName: "subscribeSms", value: subscribeSms });
    },
    initializeTracking() {
      window.mixpanel.track(TRACKING.MIXPANEL_CAC_LOAD_EVENT_NAME);
    },
    saveOptionalFields() {
      const {
        title: { isOn: title },
        email: { isOn: email },
        phone: { isOn: phone }
      } = this.settings.formFields.default.welcome;
      this.setOptionalFields({ title, email, phone });
    },
    handleResize() {
      const isMobile = window.innerWidth <= BREAKPOINTS.SM_MAX;
      if (isMobile !== this.isMobile) {
        this.setIsMobile(isMobile);
      }
      this.broadcastDimensions();
    }
  },
  render(createElement) {
    if (!this.template) {
      return createElement("div", "Loading...");
    }

    return this.template();
  }
};
</script>
